import {
  useState, useCallback, useEffect, useMemo
} from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { selectors, hooks } from 'farmx-redux-core';
import { newApiCallNeeded, useGetSensorData, buildConfigV1 } from 'farmx-web-ui';
import { sensorApi } from 'farmx-api';
import { dateFormatConfig } from './constants';
import {
  buildGraphConfig, chartRuleEngine, getDailyAverage, getMultiplierAndData,
  preparePlotLines, cutoffLineLabelStyle,
} from '../../helper/graphHelper';
import {
  tooltipBgColor,
  tooltipBorderColor,
  colorCritical,
  colorWarning,
  colorOver,
  colorOk,
} from '../../utils/colors';
import { roundToDecimalPlaces } from '../../helper/anomalyHelper';
import isEqual from 'react-fast-compare';

const { loadSensorData } = sensorApi;
const { useUnits } = hooks;
const { selectClientTimeFormat } = selectors;


const noonConfig = {
  hour: 12,
  minute: 0,
  second: 0,
  millisecond: 0
};

const useSensorGraphData = (
  sensorType,
  sensorId,
  sensorIdentifier,
  variables,
  startDate,
  endDate,
  cached,
  refresh,
  preparedConfig,
  renderOption,
  sensorsString
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({});
  const [newData, setNewData] = useState({});
  const { isGraphDataApiCallNeeded } = chartRuleEngine(renderOption, preparedConfig);

  const {
    getSensorData, loading: apiLoading, error: apiError,
    data: oldApiData, newData: newApiData, clearState,
  } = useGetSensorData();

  // load sensor graph data
  const callGetSensorData = useCallback((startDate, endDate, forceRefresh = false) => {
    // IMPORTANT: API requires sensorId (database ID), identifier alone is not sufficient
    // Only make API call if we have sensorId
    const shouldMakeApiCall = (isGraphDataApiCallNeeded || forceRefresh) && variables?.length && sensorId;

    if(shouldMakeApiCall) {
      getSensorData({
        type: sensorType,
        id: sensorId,
        identifier: sensorIdentifier
      }, variables, startDate, endDate, undefined, sensorsString);
    }
  }, [isGraphDataApiCallNeeded, sensorId, sensorType, sensorIdentifier, variables, getSensorData, sensorsString]);

  useEffect(() => {
    // call graph data api when:
    // 1. There's no preparedConfig AND we have the necessary data (sensorId is required, variables)
    // 2. OR when refresh changes (for auto-refresh)
    // IMPORTANT: sensorId (database ID) is required - identifier alone won't work for API
    const canFetch = variables?.length && sensorId;
    const shouldFetch = !preparedConfig && canFetch;
    const forceRefresh = refresh && refresh > 0 && canFetch;

    if (shouldFetch || forceRefresh) {
      // clear previous graph data
      setNewData({});
      setData({})
      clearState();

      // Fetch data for the start date and end date
      // Pass true for forceRefresh parameter when we're forcing a refresh
      callGetSensorData(startDate, endDate, forceRefresh);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, moment(startDate).format(), moment(endDate).format(),
    preparedConfig, sensorId, variables?.length])

  useEffect(() => { 
    if (!isEqual(loading, apiLoading)) setLoading(apiLoading);
    if (!isEqual(error, apiError)) setError(apiError);
    if (!isEqual(data, oldApiData)) {
      setData(oldApiData?.[`${sensorType}/${sensorIdentifier}`]);
    }
    if (newApiData && Object.keys(newApiData || {})?.length) {
      const apiData = newApiData?.[`${sensorType}/${sensorIdentifier}`];
      let graphData = {};
      variables.forEach((variable) => {
        graphData[variable] = apiData?.[variable];
      });
      if (!isEqual(newData, graphData)) {
        setNewData(graphData);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiLoading, apiError, oldApiData, newApiData]);

  return { loading, data: Object.keys(data || {})?.length ? data : newData, error };
};

function getYminMaxFromConfig(config) {
  let plotLinesArr = [];
  const minMaxArr = config?.series?.map((item) => [item.min * (item?.multiplier || 1),
    item.max * (item?.multiplier || 1)])?.flat()?.filter((d) => isFinite(d)) || [];
  if (config?.yAxis?.length) {
    plotLinesArr = config?.yAxis?.map((item) => item?.plotLines || [])?.flat()
    .map((item) => item?.value) || [];
  }
  const minVal = Math.min(...minMaxArr, ...plotLinesArr);
  const maxVal = Math.max(...minMaxArr, ...plotLinesArr);

  return {
    min: isFinite(minVal) ? minVal : 0,
    max: isFinite(maxVal) ? maxVal : 0,
  }
}

export function isValidRange(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function isBetweenZeroAndOne(value) {
  return (
    typeof value === 'number' &&
    (
      (value >= 0 && value <= 1) || // between 0 and 1 inclusive
      Number.isNaN(value) ||        // NaN
      value === Infinity || value === -Infinity
    )
  );
}

function getUpdatedYminMaxGraphConfig(config, configForYminMax) {
  let updatedGraphConfig = { ...config }
  const yMinMax = getYminMaxFromConfig(configForYminMax || config);
  if (updatedGraphConfig?.yAxis?.length) {
    updatedGraphConfig.yAxis = updatedGraphConfig.yAxis.map((item, index) => {
      const obj = { ...item };
      if (isBetweenZeroAndOne(item?.min)) obj.min = yMinMax?.min;
      if (isBetweenZeroAndOne(item?.max)) obj.max = yMinMax?.max;
      if (isBetweenZeroAndOne(updatedGraphConfig?.series?.[index]?.min)) obj.min = yMinMax?.min;
      if (isBetweenZeroAndOne(updatedGraphConfig?.series?.[index]?.max)) obj.max = yMinMax?.max;
      return obj;
    });
  }
  return updatedGraphConfig;
}




const dataGapHours = 6;

const usePrepareGraphConfig = (
  customConfig,
  graphData,
  startDate,
  endDate,
  uniformYAxis,
  yMinMax,
  chartRef,
  preparedConfig,
  renderOption,
  sensorType,
  sensorIdentifier,
) => {
  // TODO remove after testing
  // console.log('calling usePrepareGraphConfig', preparedConfig, graphData);
  const [config, setConfig] = useState();
  let type;
  let identifier;
 const getUserUnits = hooks.useUnits();
 const sensorStatus = useSelector(
    (state) => selectors.selectSensorStatus(state, type, identifier),
  );
 const clientTimeFormat = useSelector(selectClientTimeFormat);
 const isTodayRange = useMemo(() => {
   if (!startDate || !endDate) return false;
   return (
     moment(startDate).isSame(moment(), 'day')
     && moment(endDate).isSame(moment(startDate).clone().add(1, 'day'), 'day')
   );
 }, [startDate, endDate]);
 const isHourlyRange = useMemo(() => {
   if (!startDate || !endDate) return false;
   return moment(endDate).diff(moment(startDate), 'hours', true) <= 24;
 }, [startDate, endDate]);
 const xAxisLabels = {
    align: 'left',
    x: 1,
    y: 10,
    formatter: function () {
      if (this.isFirst && (isTodayRange || isHourlyRange)) {
        return moment(this.value).format('DD/HH:[00]');
      }
      const format = (isTodayRange || isHourlyRange)
        ? 'HH:[00]'
        : (dateFormatConfig[clientTimeFormat] || 'MM/DD');
      return moment(this.value).format(format);
    }
  };

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('sensorStatus changed', sensorStatus);
  // }, [sensorStatus]);

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('customConfig changed', customConfig);
  // }, [customConfig]);

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('graphData changed', graphData);
  // }, [graphData]);

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('usePrepareGraphConfig startDate changed', startDate);
  // }, [startDate]);

  // TODO remove after testing
  // useEffect(() => {
  //   console.log('preparedConfig changed', preparedConfig);
  // }, [preparedConfig]);

  // TODO remove if not useful
  // const getColor = useMemo(() => getColors(sensorStatus), [sensorStatus]);

  useEffect(() => {
    // prepare config based on rule engine response
    const { prepareTheConfig } = chartRuleEngine(renderOption, preparedConfig);
    // TODO remove after testing
    // console.log('prepareTheConfig', prepareTheConfig);
    const updatedGraphData = Object.entries(graphData).map(([key, value]) => {
      return { [key]: [value]}
    }).reduce((acc, obj) => ({ ...acc, ...obj}), {});
    if(prepareTheConfig){
      // TODO remove after testing
      // console.log('Creating config...', graphData);
      let graphConfig = Object.keys(graphData || {}).map((chartType) => {
        const chartDataList = graphData?.[chartType];
        const chartData = chartDataList?.[0] || {};

        const { sensor_type: chartSensorType, sensor_id: chartSensorIdentifier } = chartData;
        type = chartSensorType || sensorType;
        identifier = chartSensorIdentifier || sensorIdentifier;

        let config = {};
        if (newApiCallNeeded(sensorType)) {
          const updatedData = Object.entries(graphData || {}).map(([key, value]) => {
            return { 
              [key]: { 
                variable: key,
                data: value ? [value] : [],
                units: value?.units,
                originalUnit: value?.units,
                actualChartKey: key,
              }
            }
          }).reduce((acc, obj) => ({ ...acc, ...obj}), {})
          config = buildConfigV1(updatedData, false, true, dataGapHours, getUserUnits);
        } else {
          config = buildGraphConfig(graphData, getUserUnits, dataGapHours);
        }

        const defaultConfig = {
          tooltip: {
            backgroundColor: tooltipBgColor,
            borderColor: tooltipBorderColor,
            style: {
              color: 'white',
            },
          },
          legend: {
            enabled: false,
          },
          xAxis: {
            type: 'datetime',
            ordinal: false,
            gridLineWidth: 1,
            labels: xAxisLabels,
            alternateGridColor: 'rgba(200,200,200,0.1)',
            min: startDate?.valueOf(),
            max: endDate?.valueOf(),
            tickInterval: null,
            dateTimeLabelFormats: {},
          },
          plotOptions: {},
        };
        // TODO remove after testing
        // console.log('different values', defaultConfig, config);
        return { ...defaultConfig, ...config };
      }).reduce((acc, obj) => {
        const config = { ...acc, ...obj };
        return config;
      }, {});
      // TODO remove after testing
      // console.log('graphConfig', graphConfig);
      // console.log('chartRef', chartRef);

      if(Object.keys(graphConfig || {}).length){
        if (customConfig) {
          graphConfig = customConfig(graphConfig, newApiCallNeeded(sensorType)? updatedGraphData : graphData, chartRef);
        }
        const updatedGraphConfig = getUpdatedYminMaxGraphConfig({ ...graphConfig });
        // TODO remove after testing
        // console.log('setting config for hidden chart', graphConfig);
        setConfig(newApiCallNeeded(sensorType) ? updatedGraphConfig : graphConfig);
      }
    } else{
      // TODO need to fine tune this
      // console.log('calling customConfig with preparedConfig...', preparedConfig, chartRef);
      const graphConfig = customConfig(preparedConfig, preparedConfig?.graphData, chartRef);
      const updatedGraphConfig = getUpdatedYminMaxGraphConfig({ ...graphConfig });
      setConfig(newApiCallNeeded(sensorType) ? updatedGraphConfig : graphConfig);
    }
  }, [renderOption, preparedConfig, graphData, startDate, endDate, chartRef, customConfig]);
  const graphConfig = getUpdatedYminMaxGraphConfig(config);
  const updatedGraphConfig = {
    ...graphConfig,
    series: graphConfig?.series?.map((item) => ({ ...item, actualIdentifier: sensorIdentifier }))
  };
  // TODO remove after testing
  // console.log('Graph hook config', config);
  return updatedGraphConfig;
};

const usePreparedConfigForIndividualDepth = (
  preparedConfig,
  graphData,
  mode,
  prevMode,
) => {
  // TODO remove after testing
  // console.log('usePreparedConfigForIndividualDepth preparedConfig', preparedConfig);
  const updatedConfig = useMemo(() => (
    (preparedConfig && preparedConfig.series)
      ?preparedConfig.series.map(
        (seriesData) => ({
          ...preparedConfig,
          series: [{...seriesData, point: {}}],
          graphData: graphData[seriesData.actualChartKey]?.[0],
          isMenuChange: prevMode !== mode,
        })
      ):[]),
  [graphData, preparedConfig]);
  // TODO remove after testing
  // console.log('updatedConfig for each depth', updatedConfig);
  return updatedConfig;
};

/**
 * Funtion to return the VWC forecast value for each dates
 * @param {*} forecastData - Forecaseted dalily data points
 * @returns vwcForecast value for each input dates
 */
function prepareForecastData(forecastData) {
  const {
    dateDaily, forecastedDataPoints
  } = forecastData || {};

  // To filter the previous dates from forecast data
  const currentDate = moment();
  const currNoonDate = currentDate.set(noonConfig);
  const currDate = currNoonDate.valueOf();

  // Calculate the vwcForecast value for the forecastDaily dates
  const updatedForecastData = dateDaily?.map((fDate, i) => {
    const originalDate = moment(fDate);
    // This will display the date centered at noon.
    const noonDate = originalDate.set(noonConfig);
    const forecastDate = noonDate.valueOf();

    if (moment(forecastDate).isSameOrAfter(currDate)) {
      const vwcForecast = forecastedDataPoints?.[i]

      return [forecastDate, isFinite(vwcForecast) ? vwcForecast : 0];
    }
    return [];
  })?.filter((item) => item?.[0] || item?.[1]);

  return updatedForecastData;
}

function prepareForecastDataBackup(vwcData, forecastData, depthRootzone) {
  const dailYAvgData = getDailyAverage(vwcData);
  let previousVWC = dailYAvgData?.[dailYAvgData?.length - 1]?.[1];
  const {
    dateDaily, etcForecastDaily, precipForecastDaily,
    scheduledIrrigationDailyMm
  } = forecastData || {};

  // Convert depthRootzone value from "inches" to "mm"
  // 1 inch equals 25.4 mm
  const rootzoneDepthInMM = (depthRootzone || 0) * 25.4;

  // To filter the previous dates from forecast data
  const currentDate = moment();
  const currNoonDate = currentDate.set(noonConfig);
  const currDate = currNoonDate.valueOf();

  // Calculate the vwcForecast value for the forecastDaily dates
  const updatedForecastData = dateDaily?.map((fDate, i) => {
    const originalDate = moment(fDate);
    // This will display the date centered at noon.
    const noonDate = originalDate.set(noonConfig);
    const forecastDate = noonDate.valueOf();

    if (moment(forecastDate).isSameOrAfter(currDate)) {
      // Convert etcForecast value from "mm" to "percentage" based on the depthRootzone value
      const etc = etcForecastDaily?.[i] ? (etcForecastDaily?.[i] / rootzoneDepthInMM) : 0;

      // Convert precipForecast value from "mm" to "percentage" based on the depthRootzone value
      const precip = precipForecastDaily?.[i] ? (precipForecastDaily?.[i] / rootzoneDepthInMM) : 0;

      // Convert scheduledIrrigationForecast value from "mm" to "percentage"
      const scheduled = scheduledIrrigationDailyMm?.[i]
        ? (scheduledIrrigationDailyMm?.[i] / rootzoneDepthInMM) : 0;

      // Calculate vwcForecast based on previousVWC, etc, precip, and scheduledIrrigation data
      const vwcForecast = previousVWC - etc + precip + scheduled;

      // Update currentVWC for the next iteration
      previousVWC = vwcForecast;

      return [forecastDate, isFinite(vwcForecast) ? vwcForecast : 0];
    }
    return [];
  })?.filter((item) => item?.[0] || item?.[1]);

  return updatedForecastData;
}

// Funtion will prepare and return the horizontal plot lines with custom styles
// for the upper, middle and lower moister zones
function getLinesConfig(moisture_zone_upper, moisture_zone_middle,
  moisture_zone_lower, multiplier) {
  const linesConfig = [{
    color: colorOver,
    value: moisture_zone_upper * multiplier,
    type: 'upper',
    from: moisture_zone_upper * multiplier,
    to: (moisture_zone_upper * multiplier || 0) + 1 * multiplier,
    className: 'upper-band-line',
    label: {
      x: -110,
      y: 12,
      align: 'right',
      text: `FC  (${Number(moisture_zone_upper * multiplier).toFixed(1)})`,
      style: cutoffLineLabelStyle
    }
  },
  {
    color: colorWarning,
    value: moisture_zone_middle * multiplier,
    type: 'middle',
    label: {
      x: -55,
      align: 'right',
      text: `RP (${Number(moisture_zone_middle * multiplier).toFixed(1)})`,
      style: cutoffLineLabelStyle
    }
  },
  {
    color: colorCritical,
    value: moisture_zone_lower * multiplier,
    type: 'lower',
    from: 0,
    to: moisture_zone_lower * multiplier,
    className: 'lower-band-line',
    label: {
      x: 0,
      y: 12,
      align: 'right',
      text: `WP (${Number(moisture_zone_lower * multiplier).toFixed(1)})`,
      style: cutoffLineLabelStyle
    }
  }];

  return linesConfig;
}

const usePrepareVWCForecastGraphConfig = (forecastData, depthRootzone, series2Name) => {
  const customConfig = useCallback((configObj, seriesData) => {
    let plotLines = [];
    let plotBands = [];
    const rootZoneVwcData = seriesData?.soil_moisture_rootzone_vwc;

    // Calculate the vwcForecast value for the forecastDaily dates
    const updatedForecastData = prepareForecastData(forecastData);

    // Convert the VWC forcast data values to percentage
    const chartDataWithForecast = updatedForecastData
      ?.map(([cDate, cValue]) => ([cDate, roundToDecimalPlaces(cValue * 100)]));

    // Add the first forecasted date and value to the last recorded VWC data
    const newChartConfig = { ...configObj?.series?.[configObj?.series?.length - 1] };
    if (chartDataWithForecast?.length) {
      newChartConfig.data = [...newChartConfig?.data || [], ...[chartDataWithForecast?.[0]]];
    }
    const dataValues = newChartConfig?.data?.map((d) => d?.[1] || 0) || [0];
    const seriesValues = chartDataWithForecast?.map((d) => d?.[1] || 0) || [0];

    const seriesConfig = {
      data: chartDataWithForecast,
      name: series2Name,
      marker: {
        enabled: true
      },
      dashStyle: 'Dash', // Set the dashed style for the second series
      linkedTo: ':previous' // This will link it to the previous series
    };

    if (configObj && rootZoneVwcData) {
      const updatedConfig = Object.entries(seriesData).map(([key, value]) => {
        if (!value.length) return undefined;
        const chartData = value[0];
        if (!chartData.data.length) return undefined;

        const { data: variableData } = chartData;
        const { multiplier } = getMultiplierAndData(variableData, useUnits, chartData.units);

        const {
          moisture_zone_upper: moistureZoneUpper,
          moisture_zone_middle: moistureZoneMiddle,
          moisture_zone_lower: moistureZoneLower,
        } = chartData || {};

        const linesConfig = getLinesConfig(moistureZoneUpper, moistureZoneMiddle,
          moistureZoneLower, multiplier);

        const plotLinesConfig = preparePlotLines(linesConfig);
        plotLines = plotLinesConfig.plotLines;
        plotBands = plotLinesConfig.plotBands;

        // To update yAxis range when data points has 0
        const plotBandValues = plotLinesConfig?.plotLines?.map((d) => d?.value || 0);
        const yAxisMax = Math.max(...dataValues, ...plotBandValues, ...seriesValues);
        const yAxisMin = Math.min(...dataValues, ...plotBandValues, ...seriesValues);

        const yAxis = configObj.yAxis.map((d) => {
          const obj = { ...d };
          obj.plotLines = plotLines;
          obj.plotBands = plotBands;
          if (isFinite(yAxisMin)) obj.min = yAxisMin;
          if (isFinite(yAxisMax)) obj.max = yAxisMax;
          return obj;
        });

        // Change the xAxis max date range to show VWC forecast data
        const xAxisMax = chartDataWithForecast?.[chartDataWithForecast?.length - 1]?.[0];
        let xAxis = { ...configObj.xAxis };
        if (xAxisMax) xAxis = { ...configObj.xAxis, max: xAxisMax};

        return {
          ...configObj,
          series: [
            { // The VWC data
              ...newChartConfig,
              min: yAxisMin,
              max: yAxisMax,
            },
            { // The VWC Forecast data
              data: chartDataWithForecast,
              ...seriesConfig,
              min: yAxisMin,
              max: yAxisMax,
            }],
          yAxis,
          xAxis,
          plotOptions: {
            series: {
              color: colorOk,
              states: {
                hover: {
                  enabled: true // Enable hover states for both series
                }
              }
            }
          }
        };
      }).reduce((acc, obj) => {
        const config = { ...acc, ...obj };
        return config;
      }, {});

      return updatedConfig;
    }
    return configObj;
  }, [depthRootzone, forecastData, series2Name]);

  return customConfig;
};

export {
  useSensorGraphData, usePrepareGraphConfig, usePreparedConfigForIndividualDepth,
  usePrepareVWCForecastGraphConfig,
};
